import { FC, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { useUsersApi } from "../../../http/users/api";
import { User } from "../../../http/users/types";
import { NestedPick, PaginationQueryWithFields } from "../../../types";
import SvgEdit from "../../icons/Edit";
import CustomLink from "../../ui/CustomLink";
import { getDeleteAction } from "../../ui/ListItemMenu";
import Table from "../../ui/Table";
import TextColumnFilter from "../../ui/columnFilters/TextColumnFilter";
import CreateUserSubsidiaryDialog from "./CreateUserSubsidiary";
import DeleteUserSubsidiaryDialog from "./DeleteUserSubsidiary";

const USER_FIELDS = ["id", "fullName", "email", "department", "roles[]"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = NestedPick<User, UserField>;

const UserSubsidiaries: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const usersApi = useUsersApi();
  const filters = useListFilters({
    isSearchable: true,
  });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const navigate = useNavigate();
  const { id: subsidiaryId } = useParams<{ id: string }>();

  const [itemToDelete, setItemToDelete] = useState<UserSearch>();

  const query: PaginationQueryWithFields<UserSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: USER_FIELDS,
  };

  const result = usersApi.useAdminUsers(query, aborter.current.signal, subsidiaryId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: usersApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const navToEdit = (item: UserSearch) => navigate(`/admin/users/${item.id}`);

  return (
    <>
      <Table<UserSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: !!userSettings.permissions.users.update && subsidiaryId && (
            <CreateUserSubsidiaryDialog subsidiaryId={subsidiaryId} />
          ),
        }}
        head={
          <>
            <Table.Th title={i18n.t.common.name} fieldName="user.name" order={orderOptions} />
            <Table.Th title={i18n.t.common.email} fieldName="user.email" order={orderOptions} />

            <Table.Th
              title={i18n.t.common.department}
              fieldName="user.department"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.roles}
              fieldName="user.roles"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <th></th>
          </>
        }
        onRowClick={(item) => navigate(`/admin/users/${item.id}`)}
        renderRow={(item) => (
          <>
            <td>
              <CustomLink to={`/admin/users/${item.id}`}>{item.fullName}</CustomLink>
            </td>
            <td>
              <CustomLink to={`mailto:${item.email}`}>
                {item.email}
              </CustomLink>
            </td>
            <td>{item.department}</td>
            <td>{item.roles?.length ? item.roles.map((r) => r.name).join(", ") : null}</td>
          </>
        )}
        rowActions={(item) => [
          {
            title: i18n.t.common.edit,
            onClick: () => navToEdit(item),
            icon: SvgEdit,
          },
          ...(userSettings.permissions.users.update ? [getDeleteAction(i18n, () => setItemToDelete(item), true)] : []),
        ]}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && subsidiaryId && (
        <DeleteUserSubsidiaryDialog
          userId={itemToDelete.id}
          subsidiaryId={subsidiaryId}
          onClose={() => setItemToDelete(undefined)}
        />
      )}
    </>
  );
};

export default UserSubsidiaries;
